import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { useTranslation } from 'react-i18next';
import { PricingSEO } from '../components/SEO';
import { FloatingShapes } from '../components/icons/FeatureIcons';
import axios from 'axios';
import { Clock } from 'lucide-react';

const baseAPI = process.env.REACT_APP_API_URL || '/api';

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [pricingConfig, setPricingConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${baseAPI}/public/pricing`);
        setPricingConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Use default config if API fails
        setPricingConfig(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const isComingSoon = (planId) => {
    return pricingConfig?.plans?.[planId]?.coming_soon === true;
  };

  const getComingSoonText = (planId) => {
    const plan = pricingConfig?.plans?.[planId];
    if (i18n.language === 'en') {
      return plan?.coming_soon_text_en || 'Coming Soon';
    }
    return plan?.coming_soon_text || t('pages.pricing.comingSoon') || 'Yakında';
  };

  const formatPrice = (priceInKurus) => {
    if (!priceInKurus || priceInKurus === 0) return null;
    return `₺${(priceInKurus / 100).toFixed(0)}`;
  };

  return (
    <PublicLayout>
      <PricingSEO />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 to-indigo-700 py-20 overflow-hidden">
        <FloatingShapes />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <p className="text-sm font-semibold text-purple-200 uppercase tracking-wide mb-4">{t('footer.pricing')}</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6">{t('pages.pricing.title')}</h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">{t('pages.pricing.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-10 relative z-10">

        <div className="grid md:grid-cols-3 gap-6">
          {/* Starter Plan */}
          <div className={`border border-gray-200 rounded-2xl p-6 shadow-lg bg-white relative ${isComingSoon('starter') ? 'opacity-75' : ''}`}>
            {isComingSoon('starter') && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {getComingSoonText('starter')}
              </div>
            )}
            <h2 className="text-xl font-bold mb-2">{t('pages.pricing.free')}</h2>
            <p className="text-gray-600 mb-4">{t('pages.pricing.freeDesc')}</p>
            <ul className="space-y-2 text-gray-700 text-sm mb-6">
              <li>{t('pages.pricing.freeFeature1')}</li>
              <li>{t('pages.pricing.freeFeature3')}</li>
              <li>{t('pages.pricing.freeFeature4')}</li>
              <li>{t('pages.pricing.freeFeature5')}</li>
            </ul>
            <button 
              onClick={() => !isComingSoon('starter') && navigate('/signup')}
              disabled={isComingSoon('starter')}
              className={`w-full py-2 rounded-lg font-semibold transition-colors ${
                isComingSoon('starter') 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
              }`}
            >
              {isComingSoon('starter') ? getComingSoonText('starter') : t('pages.pricing.freeCta')}
            </button>
          </div>

          {/* Pro Plan */}
          <div className={`border-2 border-purple-200 rounded-2xl p-6 shadow-xl bg-gradient-to-br from-purple-50 to-indigo-50 relative ${isComingSoon('pro') ? 'opacity-75' : ''}`}>
            {isComingSoon('pro') ? (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {getComingSoonText('pro')}
              </div>
            ) : (
              <div className="inline-flex px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold mb-3">{t('pages.pricing.proPopular')}</div>
            )}
            <h2 className="text-xl font-bold mb-2">{t('pages.pricing.pro')}</h2>
            {pricingConfig?.plans?.pro?.monthly_price > 0 && !isComingSoon('pro') && (
              <div className="mb-3">
                <span className="text-3xl font-bold text-purple-700">{formatPrice(pricingConfig.plans.pro.monthly_price)}</span>
                <span className="text-gray-600 text-sm">/{t('pages.pricing.month') || 'ay'}</span>
              </div>
            )}
            <p className="text-gray-700 mb-4">{t('pages.pricing.proDesc')}</p>
            <ul className="space-y-2 text-gray-800 text-sm mb-6">
              <li>{t('pages.pricing.proFeature1')}</li>
              <li>{t('pages.pricing.proFeature2')}</li>
              <li>{t('pages.pricing.proFeature3')}</li>
              <li>{t('pages.pricing.proFeature4')}</li>
            </ul>
            <button 
              onClick={() => !isComingSoon('pro') && navigate('/signup')}
              disabled={isComingSoon('pro')}
              className={`w-full py-2 rounded-lg font-semibold transition-colors ${
                isComingSoon('pro') 
                  ? 'bg-purple-300 text-purple-100 cursor-not-allowed' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {isComingSoon('pro') ? getComingSoonText('pro') : t('pages.pricing.proCta')}
            </button>
          </div>

          {/* Enterprise Plan */}
          <div className={`border border-gray-200 rounded-2xl p-6 shadow-lg bg-white relative ${isComingSoon('enterprise') ? 'opacity-75' : ''}`}>
            {isComingSoon('enterprise') && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {getComingSoonText('enterprise')}
              </div>
            )}
            <h2 className="text-xl font-bold mb-2">{t('pages.pricing.enterprise')}</h2>
            {pricingConfig?.plans?.enterprise?.monthly_price > 0 && !isComingSoon('enterprise') && (
              <div className="mb-3">
                <span className="text-3xl font-bold text-gray-900">{formatPrice(pricingConfig.plans.enterprise.monthly_price)}</span>
                <span className="text-gray-600 text-sm">/{t('pages.pricing.month') || 'ay'}</span>
              </div>
            )}
            <p className="text-gray-600 mb-4">{t('pages.pricing.enterpriseDesc')}</p>
            <ul className="space-y-2 text-gray-700 text-sm mb-6">
              <li>{t('pages.pricing.enterpriseFeature1')}</li>
              <li>{t('pages.pricing.enterpriseFeature2')}</li>
              <li>{t('pages.pricing.enterpriseFeature3')}</li>
              <li>{t('pages.pricing.enterpriseFeature4')}</li>
              <li>{t('pages.pricing.enterpriseFeature5')}</li>
            </ul>
            <button 
              onClick={() => !isComingSoon('enterprise') && navigate('/contact')}
              disabled={isComingSoon('enterprise')}
              className={`w-full py-2 rounded-lg font-semibold transition-colors ${
                isComingSoon('enterprise') 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {isComingSoon('enterprise') ? getComingSoonText('enterprise') : t('pages.pricing.enterpriseCta')}
            </button>
          </div>
        </div>
        </div>
    </PublicLayout>
  );
};

export default Pricing;
